import { createApiService } from "@/core/api/apiService";
import type {
  CreatePaymentDto, PaymentDetailsDto, PaymentDto, PaymentPreparationDto, PaymentSessionDto, PaymentSummaryDto,
} from "../types/paymentTypes";

export function createPaymentApiService() {
  const owner = createApiService("businesses");
  const customer = createApiService("appointment-payment-sessions");
  const path = (businessId: string, appointmentId: string) =>
    `/${encodeURIComponent(businessId)}/appointments/${encodeURIComponent(appointmentId)}/payments`;
  const options = (signal?: AbortSignal) => ({ signal, cache: "no-store" as const, referrerPolicy: "no-referrer" as const });
  const publicOptions = (signal?: AbortSignal) => ({ ...options(signal), skipAuth: true, credentials: "omit" as const });
  return {
    getSummary: (businessId: string, appointmentId: string, signal?: AbortSignal) =>
      owner.get<PaymentSummaryDto>(path(businessId, appointmentId), options(signal)),
    create: (businessId: string, appointmentId: string, dto: CreatePaymentDto) =>
      owner.post<PaymentDto>(path(businessId, appointmentId), dto, options()),
    getById: (businessId: string, appointmentId: string, paymentId: string, signal?: AbortSignal) =>
      owner.get<PaymentDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}`, options(signal)),
    cancel: (businessId: string, appointmentId: string, paymentId: string, idempotencyKey: string) =>
      owner.post<PaymentDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}/cancel`,
        { idempotency_key: idempotencyKey }, options()),
    createSession: (businessId: string, appointmentId: string, paymentId: string, idempotencyKey: string, signal?: AbortSignal) =>
      owner.post<PaymentSessionDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}/customer-sessions`,
        { idempotency_key: idempotencyKey }, options(signal)),
    open: (token: string, signal?: AbortSignal) =>
      customer.post<PaymentDetailsDto>("/open", { token }, publicOptions(signal)),
    prepare: (token: string, signal?: AbortSignal) =>
      customer.post<PaymentPreparationDto>("/prepare", { token }, publicOptions(signal)),
    status: (token: string, signal?: AbortSignal) =>
      customer.post<PaymentDto>("/status", { token }, publicOptions(signal)),
  };
}
export const paymentApiService = createPaymentApiService();
